import {
  Box,
  Button,
  Dialog,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { CustomKeypad } from "@/pages/sy/components/CustomKeypad";
import { chargeOperationStyles } from "@/pages/sy/styles/ChargeOperationStyles";
import { newChargePanelStyles } from "@/pages/sy/styles/NewChargePanelStyles";

export const HeatNameModal = ({
  isLoading,
  isHeatNameDialogOpen,
  heatNameInput,
  setHeatNameInput,
  handleCloseHeatNameDialog,
  handleSaveHeatName,
}: {
  isLoading: boolean;
  isHeatNameDialogOpen: boolean;
  heatNameInput: string;
  setHeatNameInput: (heatNameInput: string) => void;
  handleCloseHeatNameDialog: () => void;
  handleSaveHeatName: (heatName: string) => void;
}) => {
  const theme = useTheme();
  const styles = useMemo(() => chargeOperationStyles(theme), [theme]);
  const newChargeStyles = useMemo(() => newChargePanelStyles(theme), [theme]);

  const [firstLetter, setFirstLetter] = useState<string>("");
  const [secondLetter, setSecondLetter] = useState<string>("");
  const [heatNumber, setHeatNumber] = useState<string>("");
  const initializedRef = useRef<boolean>(false);

  // Parse existing heat name ONLY when modal first opens
  useEffect(() => {
    if (isHeatNameDialogOpen) {
      if (heatNameInput && !initializedRef.current) {
        const match = heatNameInput.match(/^([A-Z])([A-Z])(\d{1,4})$/i);
        if (match) {
          setFirstLetter(match[1].toUpperCase());
          setSecondLetter(match[2].toUpperCase());
          setHeatNumber(match[3]); // Don't pad here - keep original digits
        } else {
          setFirstLetter("");
          setSecondLetter("");
          setHeatNumber("");
        }
        initializedRef.current = true;
      }
    } else {
      // Reset when modal closes
      initializedRef.current = false;
    }
  }, [isHeatNameDialogOpen, heatNameInput]);

  // Update parent component when any part changes
  useEffect(() => {
    if (firstLetter && secondLetter && heatNumber) {
      const fullHeatName = `${firstLetter}${secondLetter}${heatNumber}`;
      setHeatNameInput(fullHeatName);
    }
  }, [firstLetter, secondLetter, heatNumber, setHeatNameInput]);

  const handleHeatNumberChange = (input: string) => {
    const digitsOnly = input.replace(/\D/g, "").slice(0, 4);
    setHeatNumber(digitsOnly);
  };

  const handleKeyboardKeyPress = (button: string) => {
    if (button === "{clear}") {
      setHeatNumber("");
    }
  };

  const getFullHeatName = () => {
    if (!firstLetter || !secondLetter) return "";
    return `${firstLetter}${secondLetter}${heatNumber || "0000"}`;
  };

  const isValidHeatName =
    firstLetter && secondLetter && heatNumber.length === 4;

  return (
    <Dialog
      open={isHeatNameDialogOpen}
      onClose={handleCloseHeatNameDialog}
      maxWidth="md"
      fullWidth
    >
      <Box p={4}>
        <Typography variant="h5" sx={newChargeStyles.title}>
          Edit Scheduled Heat Name
        </Typography>

        <Grid container spacing={1} sx={{ mt: 1, mb: 3 }}>
          {/* Left side: Letter Selectors + Number Input */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Box>
                  {/* Heat Label */}
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Heat
                  </Typography>
                  {/* Input Section */}
                  <Grid container spacing={2} sx={{ alignItems: "center" }}>
                    {/* First Letter Selector */}
                    <Grid item sx={{ flex: "0 0 15%" }}>
                      <FormControl variant="outlined" fullWidth>
                        <Select
                          value={firstLetter}
                          onChange={(e: SelectChangeEvent<string>) =>
                            setFirstLetter(e.target.value)
                          }
                          displayEmpty
                          renderValue={(value: string) => {
                            if (!value || value === "") {
                              return (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: (theme) =>
                                      theme.palette.text.disabled,
                                  }}
                                >
                                  -
                                </Typography>
                              );
                            }
                            return value;
                          }}
                          sx={{
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            height: "70px",
                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              height: "70px",
                              padding: "0 14px",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            <Typography
                              component="span"
                              sx={{
                                color: (theme) => theme.palette.text.disabled,
                              }}
                            >
                              Select
                            </Typography>
                          </MenuItem>
                          {Array.from({ length: 26 }, (_, i) =>
                            String.fromCharCode(65 + i),
                          ).map((letter) => (
                            <MenuItem
                              key={letter}
                              value={letter}
                              sx={{
                                fontSize: "1.5rem",
                                fontWeight: 600,
                                padding: "16px 24px",
                              }}
                            >
                              {letter}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Second Letter Selector */}
                    <Grid item sx={{ flex: "0 0 15%" }}>
                      <FormControl variant="outlined" fullWidth>
                        <Select
                          value={secondLetter}
                          onChange={(e: SelectChangeEvent<string>) =>
                            setSecondLetter(e.target.value)
                          }
                          displayEmpty
                          renderValue={(value: string) => {
                            if (!value || value === "") {
                              return (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: (theme) =>
                                      theme.palette.text.disabled,
                                  }}
                                >
                                  -
                                </Typography>
                              );
                            }
                            return value;
                          }}
                          sx={{
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            height: "70px",
                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              height: "70px",
                              padding: "0 14px",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            <Typography
                              component="span"
                              sx={{
                                color: (theme) => theme.palette.text.disabled,
                              }}
                            >
                              Select
                            </Typography>
                          </MenuItem>
                          {Array.from({ length: 26 }, (_, i) =>
                            String.fromCharCode(65 + i),
                          ).map((letter) => (
                            <MenuItem
                              key={letter}
                              value={letter}
                              sx={{
                                fontSize: "1.5rem",
                                fontWeight: 600,
                                padding: "16px 24px",
                              }}
                            >
                              {letter}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Number Input */}
                    <Grid item sx={{ flex: "0 0 25%" }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={heatNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleHeatNumberChange(e.target.value)
                        }
                        placeholder="0000"
                        autoFocus
                        sx={newChargeStyles.heatInput}
                        slotProps={{
                          input: {
                            sx: {
                              fontSize: "1.5rem",
                              fontWeight: 600,
                              height: "70px",
                              "& input": {
                                textAlign: "center",
                              },
                            },
                          },
                          htmlInput: {
                            maxLength: 4,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Right side: Numeric Keypad */}
          <Grid item xs={12} md={6}>
            <Box>
              <CustomKeypad
                value={heatNumber}
                onChange={(input: string) => handleHeatNumberChange(input)}
                onKeyPress={handleKeyboardKeyPress}
                layout={{
                  default: ["7 8 9", "4 5 6", "1 2 3", "{bksp} 0 {clear}"],
                }}
                display={{
                  "{bksp}": "⌫",
                  "{clear}": "C",
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            ...(styles.buttonsContainer as object),
            display: "flex",
            pb: 2,
          }}
        >
          <Button
            variant="contained"
            color="inherit"
            onClick={handleCloseHeatNameDialog}
            sx={styles.actionButton}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleSaveHeatName(getFullHeatName());
            }}
            sx={styles.actionButton}
            disabled={isLoading || !isValidHeatName}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
